import { getMyOnboarding } from "../../../api/sellerOnboarding";

// Module-memory cache of GET /seller-onboarding/me, shared by the dashboard
// gate (SellerOnboardingGate) and the /apply wizard so a submit on /apply is
// seen by the gate without another round trip. Lives for the page session.

const REFETCH_AFTER_MS = 60 * 1000;

let cache = { uid: null, data: null, at: 0, promise: null };
const listeners = new Set();

const emit = () => listeners.forEach((fn) => fn(cache.data));

export const subscribeSellerOnboarding = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const getCachedSellerOnboarding = (uid) =>
  cache.uid === uid ? cache.data : null;

/** Store a fresh /me response (or a patched copy) for `uid`. */
export const setSellerOnboardingCache = (uid, data) => {
  cache = { uid, data, at: Date.now(), promise: null };
  emit();
};

/** Patch only the application on the cached /me (after a wizard save). */
export const updateCachedApplication = (uid, application) => {
  if (cache.uid !== uid || !cache.data) return;
  setSellerOnboardingCache(uid, { ...cache.data, application });
};

export const clearSellerOnboardingCache = () => {
  cache = { uid: null, data: null, at: 0, promise: null };
};

/**
 * Fetch /me for `uid`. Returns the cached value when it is fresh enough
 * (`maxAgeMs`; Infinity = once per session) and dedupes concurrent calls.
 */
export const loadSellerOnboarding = (uid, { maxAgeMs = Infinity } = {}) => {
  if (cache.uid === uid) {
    if (cache.promise) return cache.promise;
    if (cache.data && Date.now() - cache.at < maxAgeMs) {
      return Promise.resolve(cache.data);
    }
  } else {
    cache = { uid, data: null, at: 0, promise: null };
  }
  const promise = getMyOnboarding()
    .then((data) => {
      if (cache.uid === uid) setSellerOnboardingCache(uid, data);
      return data;
    })
    .catch((err) => {
      if (cache.uid === uid) cache.promise = null;
      throw err;
    });
  cache.promise = promise;
  return promise;
};

export const SELLER_ONBOARDING_REFETCH_MS = REFETCH_AFTER_MS;
